import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function Register() {
  const { register, error } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await register(form);
    setLoading(false);
    if (ok) {
      toast.success("Account created — welcome to ShreeDhan");
      nav("/home");
    }
  };

  return (
    <div className="min-h-screen bg-paper">
      <div className="mx-auto max-w-md px-6 py-8">
        <Link to="/" data-testid="register-back" className="inline-flex items-center gap-1 text-sm text-brand-slate">
          <ArrowLeft size={16} /> Back
        </Link>

        <div className="mt-8">
          <div className="text-xs font-bold tracking-[0.24em] uppercase text-brand-primary">Join the family</div>
          <h1 className="mt-3 font-display text-4xl font-semibold text-brand-charcoal">Create your gold vault</h1>
          <p className="mt-2 text-brand-slate">Takes less than a minute.</p>
        </div>

        <form onSubmit={onSubmit} className="mt-10 space-y-5">
          <Field label="Full name" testId="reg-name" value={form.name} onChange={onChange("name")} />
          <Field label="Email" type="email" testId="reg-email" value={form.email} onChange={onChange("email")} />
          <Field label="Phone (10 digits)" testId="reg-phone" value={form.phone} onChange={onChange("phone")} />
          <Field label="Password (min 6 chars)" type="password" testId="reg-password" value={form.password} onChange={onChange("password")} />

          {error && <div data-testid="register-error" className="text-sm text-brand-primary">{error}</div>}

          <Button
            type="submit"
            data-testid="register-submit"
            disabled={loading}
            className="w-full h-14 rounded-full bg-gold-gradient text-brand-page font-semibold text-base shadow-gold active:scale-[0.98]"
          >
            {loading ? "Creating…" : "Create account"}
          </Button>

          <div className="text-center text-sm text-brand-slate">
            Already registered?{" "}
            <Link to="/login" data-testid="register-login-link" className="text-brand-primary font-semibold underline underline-offset-4">
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, testId, type = "text", value, onChange }) {
  return (
    <div>
      <Label className="text-brand-charcoal text-sm font-semibold">{label}</Label>
      <Input
        data-testid={testId}
        type={type}
        value={value}
        onChange={onChange}
        required
        className="mt-2 h-12 rounded-xl border-brand-line bg-brand-ivory"
      />
    </div>
  );
}
